import { useCallback, useState } from "react";
import axios from "axios";
import {
  cadastrarAssinaturaService,
  GerarCobrancaAssinaturaPayload,
  GerarCobrancaAssinaturaResponse,
} from "../../../services/cadastrarAssinatura";
import { cadastrarLead } from "../../../services/cadastrarLeadService";
import { numberPadrao } from "../../../uteis/formatPhoneNumber";

type AxiosErrorData = {
  message?: string;
  error?: string;
};

type CriarLeadAssinaturaPayload = Omit<
  GerarCobrancaAssinaturaPayload,
  "pessoaId"
> & {
  nome?: string;
  celular: string;
};

type LeadCriado = {
  id: number;
  pessoaId: number;
  nome?: string | null;
  celular?: string | null;
  status?: string | null;
  tipo?: string | null;
};

type CadastroAssinaturaResultado = {
  data: GerarCobrancaAssinaturaResponse;
  variant: "success" | "warning";
  message: string;
};

const getCadastroAssinaturaFeedback = (
  data: GerarCobrancaAssinaturaResponse,
  enviarWhatsapp?: boolean
): CadastroAssinaturaResultado => {
  if (!enviarWhatsapp) {
    return {
      data,
      variant: "success",
      message: "Assinatura criada com sucesso!",
    };
  }

  if (data.whatsapp?.enviado) {
    return {
      data,
      variant: "success",
      message: "Assinatura criada e mensagem enviada com sucesso!",
    };
  }

  if (data.whatsapp?.tentado) {
    return {
      data,
      variant: "warning",
      message:
        "Assinatura criada com sucesso, mas nao foi possivel enviar a mensagem pelo WhatsApp.",
    };
  }

  return {
    data,
    variant: "warning",
    message:
      "Assinatura criada com sucesso, mas nao foi possivel confirmar o envio da mensagem.",
  };
};

export const useAdicionarAssinatura = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetError = useCallback(() => setError(null), []);

  const cadastrar = async (
    assinaturaData: CriarLeadAssinaturaPayload
  ): Promise<CadastroAssinaturaResultado | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const celularPadrao = numberPadrao(assinaturaData.celular);

      const leadPayload = {
        tipo: "LEAD" as const,
        nome: assinaturaData.nome?.trim() || undefined,
        celular: celularPadrao,
        active: 1,
        status: "DIVULGAR",
      };

      const leadCriado = (await cadastrarLead(leadPayload)) as
        | LeadCriado
        | undefined;

      if (!leadCriado?.pessoaId) {
        setError("Nao foi possivel obter o pessoaId ao cadastrar o lead.");
        return null;
      }

      const cobrancaPayload: GerarCobrancaAssinaturaPayload = {
        pessoaId: Number(leadCriado.pessoaId),
        quantidadeCotas: assinaturaData.quantidadeCotas,
        formaPagamento: assinaturaData.formaPagamento,
        planoAssinatura: assinaturaData.planoAssinatura,
        enviarWhatsapp: assinaturaData.enviarWhatsapp,
      };

      const data = await cadastrarAssinaturaService(cobrancaPayload);

      if (data?.assinatura?.id) {
        return getCadastroAssinaturaFeedback(data, assinaturaData.enviarWhatsapp);
      }

      setError("Erro ao gerar cobranca da assinatura.");
      return null;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const responseData = err.response?.data as AxiosErrorData | undefined;
        const errorMessage =
          responseData?.message ||
          responseData?.error ||
          err.message ||
          "Ocorreu um erro ao gerar a cobranca.";

        setError(errorMessage);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Ocorreu um erro ao gerar a cobranca.");
      }

      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, cadastrar, resetError };
};
